import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RefreshCw, Search, Download } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { TradingLog } from '../../types/titan';

type AutomationEvent = {
  id: string;
  timestamp: string;
  event_type: string;
  source: string;
  outcome: string;
  details?: Record<string, unknown>;
};

const LogsTab: React.FC = () => {
  const [logs, setLogs] = useState<TradingLog[]>([]);
  const [automationEvents, setAutomationEvents] = useState<AutomationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState<'system' | 'automation'>('system');
  const logEndRef = useRef<HTMLDivElement>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.logs();
      setLogs(data.logs || []);
    } catch (e) {
      console.error('Logs fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAutomation = useCallback(async () => {
    try {
      const data = await adminApi.automationActivity(200);
      const rows = (data.events || []).map((row, idx) => {
        const r = row as Record<string, unknown>;
        return {
          id: String(r.id || `${r.timestamp || 'event'}-${idx}`),
          timestamp: String(r.timestamp || new Date().toISOString()),
          event_type: String(r.event_type || 'unknown'),
          source: String(r.source || 'unknown'),
          outcome: String(r.outcome || 'unknown'),
          details: (r.details || {}) as Record<string, unknown>,
        } as AutomationEvent;
      });
      setAutomationEvents(rows);
    } catch (e) {
      console.error('Automation activity fetch failed:', e);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await Promise.all([fetchLogs(), fetchAutomation()]);
      setLoading(false);
    };
    run();
    const i = setInterval(run, 10000);
    return () => clearInterval(i);
  }, [fetchLogs, fetchAutomation]);

  useEffect(() => {
    if (activeTab === 'system') {
      logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, activeTab]);

  const filteredLogs = filter
    ? logs.filter(l => 
        l.message.toLowerCase().includes(filter.toLowerCase()) || 
        l.category.toLowerCase().includes(filter.toLowerCase()) ||
        l.level.toLowerCase().includes(filter.toLowerCase())
      )
    : logs;

  const getLogLevelClass = (level: string) => {
    const l = level.toUpperCase();
    if (l === 'ERROR') return 'bg-red-500/10 text-red-600 border-red-500/30';
    if (l === 'WARN' || l === 'WARNING') return 'bg-amber-500/10 text-amber-600 border-amber-500/30';
    if (l === 'INFO') return 'bg-blue-500/10 text-blue-600 border-blue-500/30';
    return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  };

  const filteredAutomation = filter
    ? automationEvents.filter((e) =>
        e.event_type.toLowerCase().includes(filter.toLowerCase()) ||
        e.source.toLowerCase().includes(filter.toLowerCase()) ||
        e.outcome.toLowerCase().includes(filter.toLowerCase()) ||
        JSON.stringify(e.details || {}).toLowerCase().includes(filter.toLowerCase())
      )
    : automationEvents;

  const outcomeClass = (outcome: string) => {
    const v = outcome.toLowerCase();
    if (v.includes('failed') || v.includes('error') || v.includes('exception')) {
      return 'bg-red-500/10 text-red-600 border-red-500/30';
    }
    if (v.includes('blocked') || v.includes('skip')) {
      return 'bg-amber-500/10 text-amber-600 border-amber-500/30';
    }
    return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30';
  };

  const handleDownload = () => {
    const text = logs.map(l => `[${l.timestamp}] ${l.level.toUpperCase()} [${l.category}] ${l.message}`).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `titan_system_logs_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-xs">
          <div className="relative w-full">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter logs..."
              className="w-full bg-muted/30 border-none rounded-md py-1.5 pl-8 pr-3 text-xs focus:ring-1 focus:ring-primary outline-none"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload}><Download className="w-4 h-4 mr-1" /> Export</Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              setLoading(true);
              await Promise.all([fetchLogs(), fetchAutomation()]);
              setLoading(false);
            }}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'system' | 'automation')} className="space-y-3">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="system">System Logs</TabsTrigger>
          <TabsTrigger value="automation">Automation Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="system">
          <Card className="bg-black/90 p-4 font-mono text-[11px] h-[500px] overflow-auto border-zinc-800">
            <div className="space-y-1">
              {filteredLogs.map((log) => (
                <div key={log.id || log.timestamp} className="flex gap-2 py-0.5 border-b border-zinc-900/50 hover:bg-zinc-900/50">
                  <span className="text-zinc-600 shrink-0 w-[140px]">{new Date(log.timestamp).toLocaleString()}</span>
                  <Badge variant="outline" className={`text-[9px] py-0 px-1 uppercase ${getLogLevelClass(log.level)}`}>
                    {log.level}
                  </Badge>
                  <span className="text-blue-400/80 shrink-0 min-w-[80px]">[{log.category}]</span>
                  <span className="text-zinc-300 break-all">{log.message}</span>
                </div>
              ))}
              {filteredLogs.length === 0 && (
                <div className="text-center py-20 text-zinc-700">No logs matching filter</div>
              )}
              <div ref={logEndRef} />
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="automation">
          <Card className="bg-black/90 p-4 font-mono text-[11px] h-[500px] overflow-auto border-zinc-800">
            <div className="space-y-1">
              {filteredAutomation.map((event) => (
                <div key={event.id} className="flex gap-2 py-0.5 border-b border-zinc-900/50 hover:bg-zinc-900/50">
                  <span className="text-zinc-600 shrink-0 w-[140px]">{new Date(event.timestamp).toLocaleString()}</span>
                  <Badge variant="outline" className={`text-[9px] py-0 px-1 uppercase ${outcomeClass(event.outcome)}`}>
                    {event.outcome}
                  </Badge>
                  <span className="text-cyan-400/90 shrink-0 min-w-[130px]">[{event.event_type}]</span>
                  <span className="text-zinc-400 shrink-0 min-w-[180px]">{event.source}</span>
                  <span className="text-zinc-300 break-all">{JSON.stringify(event.details || {})}</span>
                </div>
              ))}
              {filteredAutomation.length === 0 && (
                <div className="text-center py-20 text-zinc-700">No automation events matching filter</div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LogsTab;
